import type { FC } from "react";
import { useMemo, useState } from "react";

import chunk from "lodash/chunk";
import orderBy from "lodash/orderBy";

import { NoSerpData } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/components/no-serp-data/NoSerpData";
import { tableColumns } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/SERPSnapshotColumns";
import {
    Grid,
    SearchSerpContainer,
    SerpWidgetTitleText,
} from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/StyledComponents";
import type { IRecord, SERPRecords } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/types";
import { allTrackers } from "app/services/track/track";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import type { SERP_IDS } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTypes";
import type { ISortDirection } from "app/@types/userdata";
import { invertSortDirection } from "app/UtilitiesAndConstants/UtilityFunctions/sort";

import { SerpTilesLoader } from "./components/loaders/SerpTilesLoader";
import { ActiveSerpTile } from "./components/serp-tile/ActiveSerpTile";
import { StaticSerpTile } from "./components/serp-tile/StaticSerpTile";
import { SerpTileTable } from "./components/serp-tile-table/SerpTileTable";
import { DEFAULT_SORT, ITEMS_PER_PAGE, LOADER_HEIGHT, LOADER_WIDTH, SERPWithoutWebsites } from "./consts";

interface ISERPSnapshotTableTopWidgets {
    serpRecords: SERPRecords;
    noSerpRecords: boolean;
    isLoadingData: boolean;
}

export const SERPSnapshotTableTopWidgets: FC<ISERPSnapshotTableTopWidgets> = (props) => {
    const { serpRecords, noSerpRecords, isLoadingData } = props;
    const [selectedSerpFeatureName, setSelectedSerpFeatureName] = useState<SERP_IDS | "">("");
    const [sortDirection, setSortDirection] = useState<ISortDirection>("asc");
    const [page, setPage] = useState(0);
    const [pageDataChunks, setPageDataChunks] = useState<IRecord[][]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [showPagination, setShowPagination] = useState(false);

    const onTileClick = (serpName: SERP_IDS) => {
        allTrackers.dispatchTrackEvent("Table", "click", `SERP Features drilldown/${serpName}`);
        if (selectedSerpFeatureName === serpName) {
            setSelectedSerpFeatureName("");
        } else {
            setSelectedSerpFeatureName(serpName);
            const records = serpRecords[serpName].records;
            const totalRecords = records.length;
            setShowPagination(totalRecords > ITEMS_PER_PAGE);
            setTotalPages(Math.ceil(totalRecords / ITEMS_PER_PAGE));
            setPageDataChunks(chunk(records, ITEMS_PER_PAGE));
            setPage(0);
        }
    };

    const onPageChange = (selectedPage: number) => {
        const adjustedPage = selectedPage - 1;
        switch (true) {
            case adjustedPage > totalPages - 1:
                setPage(totalPages - 1);
                break;
            case adjustedPage < 0:
                setPage(0);
                break;
            case adjustedPage === page:
                return;
            default:
                setPage(adjustedPage);
                break;
        }
    };

    const getSerpCountText = (serpName: string, serpFeaturesCount: number) => {
        const textKey = SERPWithoutWebsites.includes(serpName)
            ? "serp.top.table.widgets.serp.tile.subtitle.results"
            : "serp.top.table.widgets.serp.tile.subtitle.websites";
        return translate(textKey, {
            serpFeaturesCount,
        });
    };

    const onSort = ({ sortDirection: updatedSortDirection }: { sortDirection: ISortDirection }) => {
        const sort = invertSortDirection(updatedSortDirection);
        const records = serpRecords[selectedSerpFeatureName as SERP_IDS].records;
        const sortedData = orderBy(records, "currentPosition", sort);
        setSortDirection(sort);
        setPageDataChunks(chunk(sortedData, ITEMS_PER_PAGE));
    };

    const widgetColumns = useMemo(
        () =>
            tableColumns.getWidgetTableColumnsConfig({
                sortedColumn: DEFAULT_SORT,
                sortDirection: sortDirection,
                tableName: selectedSerpFeatureName,
            }),
        [sortDirection, selectedSerpFeatureName],
    );

    const hasSerpRecords = Object.keys(serpRecords).length;

    if (isLoadingData) {
        return (
            <div>
                <SearchSerpContainer>
                    <SerpTilesLoader
                        loaderCount={4}
                        height={LOADER_HEIGHT}
                        width={LOADER_WIDTH}
                    />
                </SearchSerpContainer>
            </div>
        );
    }

    if (noSerpRecords) {
        return <NoSerpData />;
    }

    return (
        <div>
            <SearchSerpContainer>
                {!hasSerpRecords ? null : (
                    <>
                        <SerpWidgetTitleText>{i18nFilter()("serp.top.table.widgets.title")}</SerpWidgetTitleText>
                        <Grid>
                            {Object.keys(serpRecords).map((item: SERP_IDS) => {
                                const serp = serpRecords[item];
                                const isCurrentlySelected = selectedSerpFeatureName === (serp.serpFeature as SERP_IDS);
                                const itemElement = !serp.records.length ? (
                                    <StaticSerpTile serpFeature={serp} />
                                ) : (
                                    <ActiveSerpTile
                                        serpFeature={serp}
                                        isSelected={isCurrentlySelected}
                                        getSerpCountText={getSerpCountText}
                                        onTileClick={onTileClick}
                                    />
                                );

                                if (isCurrentlySelected) {
                                    return (
                                        <>
                                            {itemElement}
                                            <SerpTileTable
                                                selectedSerpFeatureName={selectedSerpFeatureName}
                                                setSelectedSerpFeatureName={setSelectedSerpFeatureName}
                                                serpRecords={serpRecords}
                                                pageDataChunks={pageDataChunks}
                                                page={page}
                                                widgetColumns={widgetColumns}
                                                onSort={onSort}
                                                showPagination={showPagination}
                                                onPageChange={onPageChange}
                                            />
                                        </>
                                    );
                                }
                                return itemElement;
                            })}
                        </Grid>
                    </>
                )}
            </SearchSerpContainer>
        </div>
    );
};
